import { __decorate, __metadata } from "tslib";
// NPM
import { Component, Emit, Mixins, Prop, Watch } from "vue-property-decorator";
// S1
import BaseMixer from "@/router/views/BaseMixer";
import StrictDateMixin from "@/router/views/StrictDateMixin";
import { ActionItemModel, ActionItemStatus, IActionItemDTO } from "@/store/models/clientUpdates/actionItem";
import ClientUpdatesModule from "@/store/modules/clientUpdates.module";
import { ACTION_ITEM_MAX_LENGTH, isBeforeDay, isBeforeToday, sameId, startOfToday, toInputDate } from "../updatesHelpers";
let AddActionItemModal = /** @class */ (() => {
    var _a;
    let AddActionItemModal = class AddActionItemModal extends Mixins(BaseMixer, StrictDateMixin) {
        constructor() {
            super(...arguments);
            this.actionItemMaxLength = ACTION_ITEM_MAX_LENGTH;
            this.selectedClientId = "";
            this.selectedTeamId = "";
            this.description = "";
            this.assigneeUserId = "";
            this.dueDate = "";
            this.loadingAssignees = false;
            // The picked client's active teams (Action Items page)
            this.teamOptions = [];
            this.loadingTeams = false;
            this.isBeforeToday = isBeforeToday;
        }
        get isEdit() {
            return !!this.actionItem;
        }
        get showClientSelect() {
            return this.allowClientSelect && !this.isEdit;
        }
        // With the client picked here there's no filter team, so the team is picked here too (required)
        get showTeamSelect() {
            return this.showClientSelect;
        }
        // Header team: the item's own (Edit) or the filter's (Updates page Add)
        get headerTeamName() {
            if (this.actionItem) {
                return this.actionItem.teamName || "";
            }
            return this.teamName || "";
        }
        // A completed item keeps its assignee
        get isCompleted() {
            return !!this.actionItem && this.actionItem.status === ActionItemStatus.Completed;
        }
        get clientName() {
            const client = (this.clientOptions || []).find((c) => sameId(c.id, this.selectedClientId));
            if (client) {
                return client.value;
            }
            return this.actionItem && this.actionItem.clientName ? this.actionItem.clientName : "";
        }
        get assigneeOptions() {
            const options = (ClientUpdatesModule.eligibleAssignees || []).map((x) => ({ id: x.id.toString(), name: x.value }));
            // Keep the current assignee selectable when editing, even if no longer eligible (the backend allows keeping them)
            if (this.actionItem && this.actionItem.assigneeUserId && !options.some((x) => sameId(x.id, this.actionItem.assigneeUserId))) {
                options.push({ id: this.actionItem.assigneeUserId.toString(), name: this.actionItem.assigneeName || "" });
            }
            return options;
        }
        // Always shown on the Action Items page; it lists teams once a client is picked
        get teamPlaceholder() {
            if (!this.selectedClientId) {
                return "Please select";
            }
            if (this.loadingTeams) {
                return "Loading...";
            }
            return this.teamOptions.length === 0 ? "No active teams" : "Please select";
        }
        get hasRequiredTeam() {
            return !this.showTeamSelect || (!this.loadingTeams && !!this.selectedTeamId);
        }
        // A typed date the picker didn't block - shown so the disabled button has a reason
        get isDueInPast() {
            return !!this.dueDate && isBeforeDay(this.dueDate, startOfToday());
        }
        get canSave() {
            return !this.saving && !this.loadingAssignees && !this.hasDateErrors && this.description.trim() !== "" && !!this.assigneeUserId && !!this.dueDate && !this.isDueInPast && !!this.selectedClientId && this.hasRequiredTeam;
        }
        // A new client means new teams - the previous pick never carries over
        onSelectedClientChange(clientId) {
            if (!this.showTeamSelect) {
                return;
            }
            this.selectedTeamId = "";
            this.teamOptions = [];
            if (!clientId) {
                this.loadingTeams = false;
                return;
            }
            this.loadingTeams = true;
            ClientUpdatesModule.GetUpdatesTeams(clientId)
                .then((data) => {
                // Ignore a late answer for a client that is no longer picked
                if (sameId(clientId, this.selectedClientId)) {
                    this.teamOptions = ((data && data.teams) || []).filter((team) => team.isActive !== false);
                }
            })
                .catch(() => {
                if (sameId(clientId, this.selectedClientId)) {
                    this.teamOptions = [];
                }
            })
                .then(() => {
                if (sameId(clientId, this.selectedClientId)) {
                    this.loadingTeams = false;
                }
            });
        }
        // Assignees don't depend on the client, so they load once and the choice survives a client change
        loadAssignees() {
            this.loadingAssignees = true;
            ClientUpdatesModule.GetEligibleAssignees()
                .catch(() => ClientUpdatesModule.SetEligibleAssignees([]))
                .then(() => {
                this.loadingAssignees = false;
            });
        }
        save() {
            if (!this.canSave) {
                return;
            }
            const model = new ActionItemModel();
            if (this.actionItem) {
                model.id = this.actionItem.id;
                model.updateId = this.actionItem.updateId;
                model.status = this.actionItem.status;
                // The server keeps the item's team on edit
                model.teamId = this.actionItem.teamId || null;
            }
            else {
                model.teamId = (this.showTeamSelect ? this.selectedTeamId : this.teamId) || null;
            }
            model.clientId = this.selectedClientId;
            model.description = this.description.trim();
            model.assigneeUserId = this.assigneeUserId;
            model.dueDate = this.dueDate;
            this.$emit("save", model);
        }
        close() { }
        created() {
            if (this.actionItem) {
                this.selectedClientId = this.actionItem.clientId ? this.actionItem.clientId.toString() : "";
                this.description = this.actionItem.description || "";
                this.assigneeUserId = this.actionItem.assigneeUserId ? this.actionItem.assigneeUserId.toString() : "";
                this.dueDate = toInputDate(this.actionItem.dueDate);
            }
            else {
                this.selectedClientId = this.clientId ? this.clientId.toString() : "";
            }
            // Assignees come with the timeline on the Updates page; otherwise load them for the item's client
            if (this.allowClientSelect || this.isEdit) {
                this.loadAssignees();
            }
        }
    };
    __decorate([
        Prop({ default: null }),
        __metadata("design:type", String)
    ], AddActionItemModal.prototype, "clientId", void 0);
    __decorate([
        Prop({ default: () => [] }),
        __metadata("design:type", Array)
    ], AddActionItemModal.prototype, "clientOptions", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], AddActionItemModal.prototype, "saving", void 0);
    __decorate([
        Prop({ default: null }),
        __metadata("design:type", typeof (_a = typeof IActionItemDTO !== "undefined" && IActionItemDTO) === "function" ? _a : Object)
    ], AddActionItemModal.prototype, "actionItem", void 0);
    __decorate([
        Prop({ default: false }),
        __metadata("design:type", Boolean)
    ], AddActionItemModal.prototype, "allowClientSelect", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], AddActionItemModal.prototype, "teamId", void 0);
    __decorate([
        Prop({ default: "" }),
        __metadata("design:type", String)
    ], AddActionItemModal.prototype, "teamName", void 0);
    __decorate([
        Watch("selectedClientId"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [String]),
        __metadata("design:returntype", void 0)
    ], AddActionItemModal.prototype, "onSelectedClientChange", null);
    __decorate([
        Emit("close"),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", []),
        __metadata("design:returntype", void 0)
    ], AddActionItemModal.prototype, "close", null);
    AddActionItemModal = __decorate([
        Component({
            name: "AddActionItemModal",
        })
    ], AddActionItemModal);
    return AddActionItemModal;
})();
export default AddActionItemModal;
